// src/components/Products.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  FaSearch, FaEdit, FaPrint, FaTag, FaTimes, FaBox, FaEye, FaPlus, FaSave, FaTimesCircle, FaUpload, FaCheck,
  FaCheckDouble, FaCity, FaChevronDown
} from 'react-icons/fa';
import './Products.css';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useCurrency } from '../../context/CurrencyContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { PrintLabelModal } from './PrintLabelModal';
import { OpenLabelModal } from './OpenLabelModal';
import { RiDeleteBin5Line } from "react-icons/ri";

import { ImplantOrderLabelModal } from '../Orders/ImplantOrderLabelModal';
import { isImplantProduct } from './ImplantLabel';
import { previewProductSku } from '../../utils/skuGenerator';
import TablePagination from "../../components/TablePagination";
// ✅ GRID STATE PERSISTENCE HELPERS
const GRID_STATE_KEY = 'products_grid_state_';
const readGridState = (id) => {
  if (!id) return {};
  try { return JSON.parse(sessionStorage.getItem(`${GRID_STATE_KEY}${id}`)) || {}; }
  catch { return {}; }
};

// ✅ PAGINATION: products per page
const PAGE_SIZE = 15;

const SearchableFilter = ({
  label,
  value,
  options,
  onChange,
  getOptionValue,
  getOptionLabel,
  count,
  placeholder = "Select an Option",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const wrapperRef = useRef(null);

  const selectedOption = options.find(
    (option) => String(getOptionValue(option)) === String(value)
  );

  useEffect(() => {
    if (selectedOption) setSearchText(getOptionLabel(selectedOption));
    else if (!value) setSearchText("");
  }, [value, options]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        if (selectedOption) setSearchText(getOptionLabel(selectedOption));
        else setSearchText("");
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [selectedOption]);

  const filteredOptions = options.filter((option) => {
    const optionLabel = String(getOptionLabel(option) || "").toLowerCase();
    const search = searchText.toLowerCase().trim();
    if (!search) return true;
    return optionLabel.includes(search);
  });

  const handleInputChange = (e) => {
    const text = e.target.value;
    setSearchText(text);
    setIsOpen(true);
    if (!text.trim()) onChange("");
  };

  const handleSelect = (option) => {
    onChange(String(getOptionValue(option)));
    setSearchText(getOptionLabel(option));
    setIsOpen(false);
  };

  const handleClear = () => { setSearchText(""); onChange(""); setIsOpen(false); };
  const handleFocus = (e) => { setIsOpen(true); if (selectedOption) e.target.select(); };

  return (
    <div className="filter-group searchable-filter-group" ref={wrapperRef}>
      <label>
        {label}{" "}
        {typeof count === "number" && <span className="filter-count">({count})</span>}
      </label>
      <div className="searchable-filter">
        <div className="searchable-filter-input-wrapper">
          <input
            type="text"
            className="searchable-filter-input"
            value={searchText}
            placeholder={placeholder}
            onFocus={handleFocus}
            onChange={handleInputChange}
            autoComplete="off"
          />
          {searchText && (
            <button type="button" className="searchable-filter-clear" onClick={handleClear}>×</button>
          )}
          <span className={`searchable-filter-arrow ${isOpen ? "open" : ""}`}>▼</span>
        </div>
        {isOpen && (
          <div className="searchable-filter-dropdown">
            <div
              className="searchable-filter-option placeholder-option"
              onMouseDown={(e) => { e.preventDefault(); handleClear(); }}
            >
              {placeholder}
            </div>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const optionValue = String(getOptionValue(option));
                return (
                  <div
                    key={optionValue}
                    className={`searchable-filter-option ${String(value) === optionValue ? "selected" : ""}`}
                    onMouseDown={(e) => { e.preventDefault(); handleSelect(option); }}
                  >
                    {getOptionLabel(option)}
                  </div>
                );
              })
            ) : (
              <div className="searchable-filter-no-results">No matching options found</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const API_BASE_URL = 'https://super-backend.hira-tech.co.uk/api/product';

const ORDER_LABEL_ONLY_FOR_IMPLANT = true;

const getCityStocks = (p) => {
  if (Array.isArray(p?.city_stocks) && p.city_stocks.length > 0) return p.city_stocks;
  if (p?.city) return [{ city: p.city, stock: p.product_Stock || 0 }];
  return [];
};

const FILTER_KEYS = ['type', 'category', 'variation', 'implantCompany', 'implantConnection'];

const FILTER_CFG = {
  type: { id: (o) => o.type_id, productId: (p) => p.type?.type_id },
  category: { id: (o) => o.catrgort_id, productId: (p) => p.product_category?.catrgort_id },
  variation: { id: (o) => o.variation_id, productId: (p) => p.product_variation?.variation_id },
  implantCompany: { id: (o) => o.company_id, productId: (p) => p.implant_company?.company_id },
  implantConnection: { id: (o) => o.brand_id, productId: (p) => p.product_brand?.brand_id },
};

const productFitsFilters = (product, f, skip) =>
  FILTER_KEYS.every((k) => {
    if (skip.includes(k) || !f[k]) return true;
    const id = FILTER_CFG[k].productId(product);
    return id != null && String(id) === String(f[k]);
  });

const brandBelongsToCompany = (brand, companyId) => {
  if (!companyId) return true;
  const cid = brand?.implant_company?.company_id ?? brand?.implant_company;
  if (cid === undefined || cid === null || cid === '') return true;
  return String(cid) === String(companyId);
};

const getShopCurrencyCode = (shop) => {
  if (!shop) return '';
  return (
    shop.currency_code ||
    shop.shop_currency ||
    shop.currency ||
    shop.base_currency ||
    shop.shop_base_currency ||
    ''
  );
};

const urlToFile = async (url, baseName) => {
  const res = await fetch(url, { mode: 'cors', cache: 'no-store' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  if (!blob || blob.size === 0) throw new Error('Empty image');
  const mime = blob.type && blob.type.startsWith('image/') ? blob.type : 'image/jpeg';
  const ext = mime.split('/')[1].split('+')[0].replace('jpeg', 'jpg') || 'jpg';
  return new File([blob], `${baseName}.${ext}`, { type: mime });
};

const CityStockSelect = ({ product, selectedCity }) => {
  const rows = getCityStocks(product);
  if (rows.length === 0) return null;
  const preferred = rows.find((r) => r.city === selectedCity) ? selectedCity : rows[0].city;
  return (
    <select
      key={`${product.product_Id}-${preferred}`}
      className="stock-city-select"
      defaultValue={preferred}
      onClick={(e) => e.stopPropagation()}
    >
      {rows.map((r) => (
        <option key={r.city} value={r.city}>{r.city}: {r.stock} units</option>
      ))}
    </select>
  );
};

const CACHE_PREFIX = 'products_cache_';

const getCachedProducts = (shopId, city) => {
  try {
    const cached = localStorage.getItem(`${CACHE_PREFIX}${shopId}_${city || 'all'}`);
    if (cached) return JSON.parse(cached);
  } catch (e) {}
  return null;
};

const setCachedProducts = (shopId, city, data) => {
  try {
    localStorage.setItem(`${CACHE_PREFIX}${shopId}_${city || 'all'}`, JSON.stringify(data));
  } catch (e) {}
};

const clearProductsCache = (shopId) => {
  try {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(`${CACHE_PREFIX}${shopId}_`)) localStorage.removeItem(key);
    });
  } catch (e) {}
};

export const prefetchAllShopsProductsGrid = async () => {
  try {
    const shopsRes = await fetch(`${API_BASE_URL}/shops/`);
    if (!shopsRes.ok) return;
    const shopsData = await shopsRes.json();
    const shops = Array.isArray(shopsData) ? shopsData : shopsData.results || [];
    await Promise.all(
      shops.map(async (shop) => {
        try {
          const res = await fetch(`${API_BASE_URL}/products/?shop_id=${shop.shop_id}`);
          if (res.ok) {
            const data = await res.json();
            const productsData = Array.isArray(data) ? data : data.results || [];
            setCachedProducts(shop.shop_id, null, productsData);
          }
        } catch (err) {}
      })
    );
  } catch (err) {}
};

const Product = () => {
  const {
    formatPrice,
    formatPriceFromCode,
    formatInShopCurrency,
    currency,
    shopBaseCurrency,
    shopBaseSymbol,
    shopCurrencySymbol,
    getCurrencySymbol,
    getCurrentCurrency,
    getCurrentCurrencySymbol,
  } = useCurrency();

  const navigate = useNavigate();
  const location = useLocation();

  const getShopIdFromSources = () => {
    const pathMatch = location.pathname.match(/\/shop\/(\d+)/);
    if (pathMatch) return pathMatch[1];
    return localStorage.getItem('selectedShopId') || localStorage.getItem('selected_shop_id') || null;
  };

  const initialShopId = getShopIdFromSources();
  const savedGrid = useRef(readGridState(initialShopId)).current;

  // ✅ Use saved state
  const [searchTerm, setSearchTerm] = useState(savedGrid.searchTerm ?? '');
  const [filters, setFilters] = useState(savedGrid.filters ?? {
    type: '', category: '', variation: '', implantCompany: '', implantConnection: '', city: 'all'
  });

  // ✅ PAGINATION: current page (restored from saved state)
  const [page, setPage] = useState(savedGrid.page ?? 1);

  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [selectMode, setSelectMode] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);

  const [showMoveModal, setShowMoveModal] = useState(false);
  const [productToMove, setProductToMove] = useState(null);
  const [moveTargetShopId, setMoveTargetShopId] = useState('');
  const [moveTargetCities, setMoveTargetCities] = useState([]);
  const [moveTargetCurrency, setMoveTargetCurrency] = useState('');
  const [movePrice, setMovePrice] = useState('');
  const [moveRows, setMoveRows] = useState([{ city: '', stock: '' }]);
  const [moveLoading, setMoveLoading] = useState(false);

  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const cityDropdownRef = useRef(null);

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [products, setProducts] = useState(() => {
    const startShopId = getShopIdFromSources();
    return (startShopId && getCachedProducts(startShopId, 'all')) || [];
  });
  const [error, setError] = useState(null);

  const [dataLoading, setDataLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(() => {
    const startShopId = getShopIdFromSources();
    const cached = startShopId && getCachedProducts(startShopId, 'all');
    return !!(cached && cached.length > 0);
  });
  const dataRequestRef = useRef(0);

  const [shopId, setShopId] = useState(() => getShopIdFromSources());
  const [shopName, setShopName] = useState('');
  const [shops, setShops] = useState([]);
  const [shopCities, setShopCities] = useState([]);

  const [showOpenLabelModal, setShowOpenLabelModal] = useState(false);
  const [showPrintLabelModal, setShowPrintLabelModal] = useState(false);
  const [showImplantOrderLabel, setShowImplantOrderLabel] = useState(false);
  const [labelProduct, setLabelProduct] = useState(null);

  const [allTypes, setAllTypes] = useState([]);
  const [allVariations, setAllVariations] = useState([]);
  const [allImplantCompanies, setAllImplantCompanies] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [allBrands, setAllBrands] = useState([]);

  const [types, setTypes] = useState([]);
  const [variations, setVariations] = useState([]);
  const [implantCompanies, setImplantCompanies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  const [modalTypes, setModalTypes] = useState([]);
  const [modalVariations, setModalVariations] = useState([]);
  const [modalBrands, setModalBrands] = useState([]);

  const [formData, setFormData] = useState({
    name: '', description: '', sku: '', reference: '', rawMaterial: '', features: '',
    type: '', category: '', variation: '', implantCompany: '', implantConnection: '',
    price: '',
    gtin: '', gtinImageFile: null, gtinImagePreview: '',
    cityStockRows: [{ city: '', stock: '' }],
    imagePreviews: ['', '', '', ''],
    imageFiles: [null, null, null, null]
  });

  // ✅ Save state (now includes page)
  useEffect(() => {
    if (!shopId) return;
    try {
      sessionStorage.setItem(
        `${GRID_STATE_KEY}${shopId}`,
        JSON.stringify({ searchTerm, filters, page })
      );
    } catch (e) {}
  }, [shopId, searchTerm, filters, page]);

  const getFilterOptions = (key, f) => {
    const baseMap = {
      type: types,
      category: categories,
      variation: variations,
      implantCompany: implantCompanies,
      implantConnection: brands,
    };
    let list = baseMap[key] || [];

    if (key === 'implantConnection') {
      list = list.filter((b) => brandBelongsToCompany(b, f.implantCompany));
    }

    const skip = key === 'implantConnection' ? ['implantConnection', 'implantCompany'] : [key];
    const hasOtherFilter = FILTER_KEYS.some((k) => !skip.includes(k) && f[k]);
    if (!hasOtherFilter) return list;

    const ids = new Set();
    products.forEach((p) => {
      if (productFitsFilters(p, f, skip)) {
        const id = FILTER_CFG[key].productId(p);
        if (id !== undefined && id !== null) ids.add(String(id));
      }
    });
    return list.filter((o) => ids.has(String(FILTER_CFG[key].id(o))));
  };

  const typeOptions = useMemo(() => getFilterOptions('type', filters), [filters, products, types, categories, variations, implantCompanies, brands]);
  const categoryOptions = useMemo(() => getFilterOptions('category', filters), [filters, products, types, categories, variations, implantCompanies, brands]);
  const variationOptions = useMemo(() => getFilterOptions('variation', filters), [filters, products, types, categories, variations, implantCompanies, brands]);
  const companyOptions = useMemo(() => getFilterOptions('implantCompany', filters), [filters, products, types, categories, variations, implantCompanies, brands]);
  const connectionOptions = useMemo(() => getFilterOptions('implantConnection', filters), [filters, products, types, categories, variations, implantCompanies, brands]);

  const fetchShops = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/shops/`);
      const shopsData = Array.isArray(response.data) ? response.data : [];
      setShops(shopsData);
      return shopsData;
    } catch (error) { return []; }
  };

  const fetchShopCities = async (sid) => {
    if (!sid) return;
    try {
      const response = await axios.get(`${API_BASE_URL}/shop-cities/?shop_id=${sid}`);
      setShopCities(response.data?.cities || []);
    } catch (error) { setShopCities([]); }
  };

  useEffect(() => {
    const init = async () => {
      const shopsData = await fetchShops();
      const urlShopId = getShopIdFromSources();
      if (urlShopId) setShopId(urlShopId);
      else if (shopsData.length > 0) {
        const firstShopId = shopsData[0].shop_id;
        localStorage.setItem('selectedShopId', firstShopId);
        navigate(`/shop/${firstShopId}/products`, { replace: true });
      }
    };
    init();
  }, []);

  useEffect(() => {
    const urlShopId = getShopIdFromSources();
    if (urlShopId && String(urlShopId) !== String(shopId)) {
      setProducts([]);
      setSelectedProducts([]);
      setSelectMode(false);
      const cachedData = getCachedProducts(urlShopId, 'all');
      if (cachedData && cachedData.length > 0) {
        setProducts(cachedData);
        setDataLoaded(true);
      } else {
        setDataLoaded(false);
      }
      setShopId(urlShopId);
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleShopChanged = (event) => {
      if (event.detail?.shopId) {
        const newShopId = event.detail.shopId.toString();
        setProducts([]);
        setSelectedProducts([]);
        setSelectMode(false);
        const cachedData = getCachedProducts(newShopId, 'all');
        if (cachedData) { setProducts(cachedData); setDataLoaded(true); }
        else { setDataLoaded(false); }
        setShopId(newShopId);
      }
    };
    window.addEventListener('shopChanged', handleShopChanged);
    return () => window.removeEventListener('shopChanged', handleShopChanged);
  }, []);

  useEffect(() => { if (shopId) fetchShopCities(shopId); }, [shopId]);

  useEffect(() => {
    if (shopId && shops.length > 0) {
      const shop = shops.find(s => String(s.shop_id) === String(shopId));
      setShopName(shop ? shop.shop_name : `Shop ${shopId}`);
    }
  }, [shopId, shops]);

  useEffect(() => {
    const handler = (e) => {
      if (cityDropdownRef.current && !cityDropdownRef.current.contains(e.target)) {
        setShowCityDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filterDataByCity = (data, city) => {
    if (!city || city === 'all' || city === '') return data;
    return data.filter(item => (item.city || item.selected_city || '') === city);
  };

  useEffect(() => {
    const loadAllData = async () => {
      if (!shopId) return;
      const requestId = ++dataRequestRef.current;
      setError(null);
      setDataLoading(true);
      try {
        let productsUrl = `${API_BASE_URL}/products/?shop_id=${shopId}`;
        if (filters.city && filters.city !== 'all' && filters.city !== '') {
          productsUrl += `&city=${encodeURIComponent(filters.city)}`;
        }
        const [productsRes, allTypesRes, allVariationsRes, allImplantCompaniesRes, allCategoriesRes, allBrandsRes] = await Promise.allSettled([
          axios.get(productsUrl),
          axios.get(`${API_BASE_URL}/types/?shop_id=${shopId}`),
          axios.get(`${API_BASE_URL}/variations/?shop_id=${shopId}`),
          axios.get(`${API_BASE_URL}/implant-companies/?shop_id=${shopId}`),
          axios.get(`${API_BASE_URL}/categories/?shop_id=${shopId}`),
          axios.get(`${API_BASE_URL}/brands/?shop_id=${shopId}`)
        ]);
        if (requestId !== dataRequestRef.current) return;

        if (productsRes.status === 'fulfilled') {
          const productsData = Array.isArray(productsRes.value.data) ? productsRes.value.data : [];
          setProducts(productsData);
          setDataLoaded(true);
          setCachedProducts(shopId, filters.city || 'all', productsData);
        } else {
          setDataLoaded(true);
        }
        if (allTypesRes.status === 'fulfilled') { setAllTypes(allTypesRes.value.data); setTypes(filterDataByCity(allTypesRes.value.data, filters.city)); }
        if (allVariationsRes.status === 'fulfilled') { setAllVariations(allVariationsRes.value.data); setVariations(filterDataByCity(allVariationsRes.value.data, filters.city)); }
        if (allImplantCompaniesRes.status === 'fulfilled') { setAllImplantCompanies(allImplantCompaniesRes.value.data); setImplantCompanies(filterDataByCity(allImplantCompaniesRes.value.data, filters.city)); }
        if (allCategoriesRes.status === 'fulfilled') { setAllCategories(allCategoriesRes.value.data); setCategories(filterDataByCity(allCategoriesRes.value.data, filters.city)); }
        if (allBrandsRes.status === 'fulfilled') { setAllBrands(allBrandsRes.value.data); setBrands(filterDataByCity(allBrandsRes.value.data, filters.city)); }
      } catch (err) {
        if (requestId !== dataRequestRef.current) return;
        setError('Failed to load data');
      } finally {
        if (requestId === dataRequestRef.current) setDataLoading(false);
      }
    };
    loadAllData();
  }, [shopId, filters.city]);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http') || imagePath.startsWith('data:')) return imagePath;
    return `https://super-backend.hira-tech.co.uk${imagePath.startsWith('/') ? '' : '/'}${imagePath}`;
  };

  const handleSearch = (e) => setSearchTerm(e.target.value);

  const handleSelectAllClick = () => {
    if (!selectMode) {
      setSelectMode(true);
      setSelectedProducts(filteredProducts.map(p => p.product_Id));
    } else {
      setSelectMode(false);
      setSelectedProducts([]);
    }
  };

  const toggleProductSelect = (productId) => {
    setSelectedProducts(prev => prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId]);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => {
      if (filterName === 'city') {
        return { ...prev, city: value, type: '', category: '', variation: '', implantCompany: '', implantConnection: '' };
      }

      const next = { ...prev, [filterName]: value };

      FILTER_KEYS.forEach((k) => {
        if (k === filterName || !next[k]) return;
        const stillValid = getFilterOptions(k, next).some(
          (o) => String(FILTER_CFG[k].id(o)) === String(next[k])
        );
        if (!stillValid) next[k] = '';
      });

      return next;
    });
  };

  const clearFilters = () => {
    setFilters({ type: '', category: '', variation: '', implantCompany: '', implantConnection: '', city: 'all' });
    setSearchTerm('');
  };

  const openAddModal = () => {
    setModalMode('add');
    const defaultCity = filters.city && filters.city !== 'all' ? filters.city : '';
    setFormData({
      name: '', description: '', sku: '', reference: '', rawMaterial: '', features: '',
      type: '', category: '', variation: '', implantCompany: '', implantConnection: '',
      price: '',
      gtin: '', gtinImageFile: null, gtinImagePreview: '',
      cityStockRows: [{ city: defaultCity, stock: '' }],
      imagePreviews: ['', '', '', ''],
      imageFiles: [null, null, null, null]
    });
    setModalTypes(allTypes);
    setModalVariations(allVariations);
    setModalBrands([]);
    setShowModal(true);
  };

  const handleEditProduct = (product) => {
    // ✅ Save ID for scroll restoration on grid
    sessionStorage.setItem('products_last_edited', String(product.product_Id));
    navigate(`/shop/${shopId}/productlist/edit/${product.product_Id}`, {
      state: { product: product, shopId: shopId, shopName: shopName, from: 'products' }
    });
  };

  const handleDeleteProduct = async (product) => {
    const stocks = getCityStocks(product);

    if (stocks.length === 0) {
      const result = await Swal.fire({
        title: 'Delete Product?',
        text: `Delete "${product.product_Name}" completely?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc2626',
        cancelButtonColor: '#64748b',
        confirmButtonText: 'Yes, Delete',
        cancelButtonText: 'Cancel'
      });
      if (!result.isConfirmed) return;

      try {
        await axios.delete(`${API_BASE_URL}/products/${product.product_Id}/delete/`);
        clearProductsCache(shopId);
        await reloadProducts();
        Swal.fire({ icon: 'success', title: 'Deleted!', text: 'Product deleted', timer: 1500, showConfirmButton: false });
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to delete' });
      }
      return;
    }

    const cityOptions = stocks.map(cs => `
      <label style="display:flex;align-items:center;gap:10px;padding:10px;border:1px solid #e5e7eb;border-radius:6px;margin-bottom:6px;cursor:pointer;text-align:left;background:#fff;">
        <input type="radio" name="delcity" value="${cs.city}" />
        <span style="font-weight:600;color:#004aad;flex:1;">${cs.city}</span>
        <span style="color:#64748b;font-size:0.85rem;">${cs.stock} units</span>
      </label>
    `).join('');

    const result = await Swal.fire({
      title: 'Delete Product',
      html: `
        <p style="margin-bottom:12px;color:#64748b;font-size:0.9rem;">
          This product has stock in multiple cities. Select which to delete:
        </p>
        <div style="display:flex;flex-direction:column;">
          ${cityOptions}
          <label style="display:flex;align-items:center;gap:10px;padding:10px;border:1px solid #fecaca;background:#fef2f2;border-radius:6px;cursor:pointer;text-align:left;margin-top:4px;">
            <input type="radio" name="delcity" value="__ALL__" />
            <span style="font-weight:700;color:#dc2626;">Delete completely (all cities)</span>
          </label>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      preConfirm: () => {
        const sel = document.querySelector('input[name="delcity"]:checked');
        if (!sel) { Swal.showValidationMessage('Please select a city'); return false; }
        return sel.value;
      }
    });

    if (!result.isConfirmed || !result.value) return;
    const chosen = result.value;

    if (chosen === '__ALL__') {
      try {
        await axios.delete(`${API_BASE_URL}/products/${product.product_Id}/delete/`);
        clearProductsCache(shopId);
        await reloadProducts();
        Swal.fire({ icon: 'success', title: 'Deleted!', text: 'Product deleted completely', timer: 1500, showConfirmButton: false });
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to delete' });
      }
    } else {
      const remaining = stocks.filter(cs => cs.city !== chosen);
      try {
        const fd = new FormData();
        fd.append('product_Name', product.product_Name || '');
        fd.append('product_Description', product.product_Description || '');
        fd.append('product_Price', product.product_Price || 0);
        fd.append('SKU', product.SKU || '');
        fd.append('shop_id', shopId);
        fd.append('product_isNew', false);
        fd.append('city_stocks', JSON.stringify(remaining));
        if (remaining.length > 0) {
          fd.append('city', remaining[0].city);
          fd.append('product_Stock', remaining.reduce((s, c) => s + c.stock, 0));
        } else {
          fd.append('product_Stock', 0);
        }

        await axios.put(`${API_BASE_URL}/products/${product.product_Id}/update/`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        clearProductsCache(shopId);
        await reloadProducts();
        Swal.fire({ icon: 'success', title: 'Removed!', text: `Stock removed for ${chosen}`, timer: 1500, showConfirmButton: false });
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to remove city stock' });
      }
    }
  };

  const reloadProducts = async () => {
    let productsUrl = `${API_BASE_URL}/products/?shop_id=${shopId}`;
    if (filters.city && filters.city !== 'all' && filters.city !== '') {
      productsUrl += `&city=${encodeURIComponent(filters.city)}`;
    }
    const response = await axios.get(productsUrl);
    setProducts(response.data);
    setCachedProducts(shopId, filters.city || 'all', response.data);
  };

  const handleOpenLabel = (product) => { setLabelProduct(product); setShowOpenLabelModal(true); };
  const handlePrintLabel = (product) => { setLabelProduct(product); setShowPrintLabelModal(true); };

  const handleImplantOrderLabel = (product) => {
    setLabelProduct(product);
    setShowImplantOrderLabel(true);
  };

  const closeModal = () => { setShowModal(false); setSelectedProduct(null); };

  const otherShops = shops.filter((s) => String(s.shop_id) !== String(shopId));

  const resetMoveForm = () => {
    setMoveTargetShopId('');
    setMoveTargetCities([]);
    setMoveTargetCurrency('');
    setMovePrice('');
    setMoveRows([{ city: '', stock: '' }]);
  };

  const openMoveModal = (product) => {
    setProductToMove(product);
    resetMoveForm();
    setShowMoveModal(true);
  };

  const closeMoveModal = () => {
    if (moveLoading) return;
    setShowMoveModal(false);
    setProductToMove(null);
    resetMoveForm();
  };

  const handleMoveShopSelect = async (sid) => {
    const idStr = String(sid);
    setMoveTargetShopId(idStr);
    setMoveRows([{ city: '', stock: '' }]);
    setMoveTargetCities([]);
    setMovePrice('');

    const shopFromList = shops.find((s) => String(s.shop_id) === idStr);
    let code = getShopCurrencyCode(shopFromList);
    if (!code) {
      try {
        const shopRes = await axios.get(`${API_BASE_URL}/shops/${idStr}/`);
        code = getShopCurrencyCode(shopRes.data);
      } catch (err) {}
    }
    setMoveTargetCurrency(code || '');

    try {
      const response = await axios.get(`${API_BASE_URL}/shop-cities/?shop_id=${idStr}`);
      setMoveTargetCities(response.data?.cities || []);
    } catch (err) {
      setMoveTargetCities([]);
    }
  };

  const addMoveRow = () => setMoveRows(prev => [...prev, { city: '', stock: '' }]);
  const removeMoveRow = (i) => setMoveRows(prev => prev.filter((_, idx) => idx !== i));
  const updateMoveRow = (i, field, value) => {
    setMoveRows(prev => {
      const rows = [...prev];
      rows[i] = { ...rows[i], [field]: value };
      return rows;
    });
  };

  const handleMoveSubmit = async () => {
    if (moveLoading || !productToMove) return;

    if (!moveTargetShopId) {
      Swal.fire({ title: 'Select Shop', text: 'Please select a shop first.', icon: 'warning', confirmButtonColor: '#004aad' });
      return;
    }

    if (movePrice === '' || isNaN(parseFloat(movePrice)) || parseFloat(movePrice) < 0) {
      Swal.fire({
        title: 'Invalid Price',
        text: `Please enter a valid price${moveTargetCurrency ? ` in ${moveTargetCurrency}` : ''}.`,
        icon: 'warning',
        confirmButtonColor: '#004aad'
      });
      return;
    }

    const rows = moveRows || [];
    if (rows.length === 0) {
      Swal.fire({ title: 'Missing City', text: 'Please add at least one city with stock.', icon: 'warning', confirmButtonColor: '#004aad' });
      return;
    }
    for (const row of rows) {
      if (!row.city || !row.city.trim()) {
        Swal.fire({ title: 'Missing City', text: 'Every row must have a city selected.', icon: 'warning', confirmButtonColor: '#004aad' });
        return;
      }
      if (row.stock === '' || isNaN(parseInt(row.stock)) || parseInt(row.stock) < 0) {
        Swal.fire({ title: 'Invalid Stock', text: `Please enter a valid stock for ${row.city}.`, icon: 'warning', confirmButtonColor: '#004aad' });
        return;
      }
    }
    const citySet = new Set();
    for (const row of rows) {
      const key = row.city.trim().toLowerCase();
      if (citySet.has(key)) {
        Swal.fire({ title: 'Duplicate City', text: `"${row.city}" is selected more than once.`, icon: 'warning', confirmButtonColor: '#004aad' });
        return;
      }
      citySet.add(key);
    }

    setMoveLoading(true);
    try {
      const p = productToMove;
      const cityStocks = rows.map((r) => ({ city: r.city.trim(), stock: parseInt(r.stock) || 0 }));
      const totalStock = cityStocks.reduce((sum, r) => sum + r.stock, 0);

      const fd = new FormData();
      fd.append('product_Name', p.product_Name || '');
      fd.append('product_Description', p.product_Description || '');
      fd.append('product_Price', parseFloat(movePrice) || 0);
      fd.append('product_Stock', totalStock);
      fd.append('city_stocks', JSON.stringify(cityStocks));
      fd.append('city', cityStocks[0].city);
      fd.append('SKU', '');

      if (p.reference) fd.append('reference', p.reference);
      if (p.gtin) fd.append('gtin', p.gtin);
      if (p.product_category?.category_Name) fd.append('category_Name', p.product_category.category_Name);
      if (p.type?.type_name) fd.append('type_name', p.type.type_name);
      if (p.product_variation?.variation_name) fd.append('variation_name', p.product_variation.variation_name);
      if (p.implant_company?.company_name) fd.append('implant_Company', p.implant_company.company_name);
      if (p.product_brand?.brand_name) fd.append('brand_Name', p.product_brand.brand_name);
      if (p.rawMaterial) fd.append('rawMaterial', p.rawMaterial);
      if (p.features) fd.append('features', p.features);

      const gallery = p.imageGallery || {};
      const failedImages = [];
      for (let i = 1; i <= 4; i++) {
        const imgUrl = getImageUrl(gallery[`image${i}`]);
        if (!imgUrl) continue;
        try {
          const file = await urlToFile(imgUrl, `image${i}`);
          fd.append(`image${i}`, file, file.name);
        } catch (imgErr) {
          console.error(`Move To: image${i} copy nahi ho saki ->`, imgUrl, imgErr);
          failedImages.push(`Image ${i}`);
        }
      }

      const gtinImgUrl = getImageUrl(p.gtin_image);
      if (gtinImgUrl) {
        try {
          const file = await urlToFile(gtinImgUrl, 'gtin_image');
          fd.append('gtin_image', file, file.name);
        } catch (imgErr) {
          console.error('Move To: barcode image copy nahi ho saki ->', gtinImgUrl, imgErr);
          failedImages.push('Barcode image');
        }
      }

      fd.append('product_isNew', true);
      fd.append('shop_id', moveTargetShopId);

      await axios.post(`${API_BASE_URL}/products/`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      clearProductsCache(moveTargetShopId);
      setSelectedProducts(prev => prev.filter(id => id !== p.product_Id));
      setShowMoveModal(false);
      setProductToMove(null);
      resetMoveForm();

      if (failedImages.length > 0) {
        Swal.fire({
          icon: 'warning',
          title: 'Moved (images missing)',
          text: `Product copy ho gaya, lekin ${failedImages.join(', ')} copy nahi ho saki. Backend ki CORS setting check karein (console mein detail hai).`,
          confirmButtonColor: '#004aad'
        });
      } else {
        Swal.fire({ icon: 'success', title: 'Moved!', text: 'Product copied to the selected shop', timer: 1800, showConfirmButton: false });
      }
    } catch (err) {
      const data = err.response?.data || {};
      let title = 'Error';
      let text = data.error || err.message || 'Move failed';

      if (data.field === 'product_Name') {
        title = 'Duplicate Product Name';
        text = `Product "${productToMove?.product_Name}" already exists in the selected shop. Please use a different name.`;
      } else if (data.field === 'SKU') {
        title = 'Duplicate SKU';
        text = data.error || 'SKU already exists in the selected shop. Please try again.';
      }

      Swal.fire({
        icon: 'warning',
        title,
        text,
        confirmButtonColor: '#004aad'
      });
    } finally {
      setMoveLoading(false);
    }
  };

  const generateCurrentProductSku = (categoryId, typeId, variationId) => {
    const category = allCategories.find((item) => String(item.catrgort_id) === String(categoryId));
    const type = allTypes.find((item) => String(item.type_id) === String(typeId));
    const variation = allVariations.find((item) => String(item.variation_id) === String(variationId));

    return previewProductSku({
      categoryName: category?.category_Name || '',
      typeName: type?.type_name || '',
      variationName: variation?.variation_name || '',
    });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;

    if (name === 'variation') {
      setFormData((prev) => ({
        ...prev,
        variation: value,
        sku: generateCurrentProductSku(prev.category, prev.type, value),
      }));
      return;
    }

    if (name === 'gtin') {
      setFormData((prev) => ({ ...prev, gtin: value.replace(/\D/g, '').slice(0, 14) }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const addCityStockRow = () => setFormData(prev => ({ ...prev, cityStockRows: [...prev.cityStockRows, { city: '', stock: '' }] }));
  const removeCityStockRow = (i) => setFormData(prev => ({ ...prev, cityStockRows: prev.cityStockRows.filter((_, idx) => idx !== i) }));
  const updateCityStockRow = (i, field, value) => {
    setFormData(prev => {
      const rows = [...prev.cityStockRows];
      rows[i] = { ...rows[i], [field]: value };
      return { ...prev, cityStockRows: rows };
    });
  };

  const handleModalCompanyChange = (e) => {
    const newCompanyId = e.target.value;
    setFormData(prev => ({ ...prev, implantCompany: newCompanyId, implantConnection: '' }));
    setModalBrands(newCompanyId ? allBrands.filter(b => b.implant_company && String(b.implant_company.company_id) === String(newCompanyId)) : []);
  };

  const handleModalCategoryChange = (e) => {
    const categoryId = e.target.value;

    setFormData((prev) => ({
      ...prev,
      category: categoryId,
      type: '',
      variation: '',
      sku: generateCurrentProductSku(categoryId, '', ''),
    }));

    setModalTypes(allTypes);
    setModalVariations(allVariations);
  };

  const handleModalTypeChange = (e) => {
    const typeId = e.target.value;

    setFormData((prev) => ({
      ...prev,
      type: typeId,
      variation: '',
      sku: generateCurrentProductSku(prev.category, typeId, ''),
    }));

    setModalVariations(allVariations);
  };

  const handleImageUpload = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const previews = [...formData.imagePreviews];
        previews[index] = reader.result;
        const files = [...formData.imageFiles];
        files[index] = file;
        setFormData({ ...formData, imagePreviews: previews, imageFiles: files });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGtinImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData((prev) => ({ ...prev, gtinImageFile: file, gtinImagePreview: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const clearGtinImage = () => {
    setFormData((prev) => ({ ...prev, gtinImageFile: null, gtinImagePreview: '' }));
  };

  const handleSubmit = async () => {
    try {
      const rows = formData.cityStockRows || [];
      if (rows.length === 0) {
        Swal.fire({ title: 'Missing City', text: 'Please add at least one city with stock.', icon: 'warning', confirmButtonColor: '#004aad' });
        return;
      }
      for (const row of rows) {
        if (!row.city || !row.city.trim()) {
          Swal.fire({ title: 'Missing City', text: 'Every row must have a city selected.', icon: 'warning', confirmButtonColor: '#004aad' });
          return;
        }
        if (row.stock === '' || isNaN(parseInt(row.stock)) || parseInt(row.stock) < 0) {
          Swal.fire({ title: 'Invalid Stock', text: `Please enter a valid stock for ${row.city}.`, icon: 'warning', confirmButtonColor: '#004aad' });
          return;
        }
      }
      const citySet = new Set();
      for (const row of rows) {
        const key = row.city.trim().toLowerCase();
        if (citySet.has(key)) {
          Swal.fire({ title: 'Duplicate City', text: `"${row.city}" is selected more than once.`, icon: 'warning', confirmButtonColor: '#004aad' });
          return;
        }
        citySet.add(key);
      }

      const cityStocks = rows.map((r) => ({ city: r.city.trim(), stock: parseInt(r.stock) || 0 }));
      const totalStock = cityStocks.reduce((sum, r) => sum + r.stock, 0);

      const trimmedName = (formData.name || '').trim();
      if (!trimmedName) {
        Swal.fire({
          title: 'Product Name Required',
          text: 'Please enter a product name.',
          icon: 'warning',
          confirmButtonColor: '#004aad'
        });
        return;
      }

      const duplicateProduct = products.find(
        (p) =>
          String(p.shop_id || p.shop?.shop_id || '') === String(shopId) &&
          (p.product_Name || '').trim().toLowerCase() === trimmedName.toLowerCase()
      );

      if (duplicateProduct) {
        Swal.fire({
          icon: 'warning',
          title: 'Duplicate Product Name',
          text: `"${trimmedName}" already exists in this shop. Please use a different name.`,
          confirmButtonColor: '#004aad'
        });
        return;
      }

      const fd = new FormData();
      fd.append('product_Name', trimmedName);
      fd.append('product_Description', formData.description);
      fd.append('product_Price', parseFloat(formData.price) || 0);
      fd.append('product_Stock', totalStock);
      fd.append('city_stocks', JSON.stringify(cityStocks));
      fd.append('city', cityStocks[0].city);
      fd.append('SKU', '');

      if (formData.reference && formData.reference.trim()) fd.append('reference', formData.reference.trim());

      if (formData.gtin && formData.gtin.trim()) fd.append('gtin', formData.gtin.trim());
      if (formData.gtinImageFile) fd.append('gtin_image', formData.gtinImageFile);

      if (formData.category) {
        const category = allCategories.find(c => String(c.catrgort_id) === String(formData.category));
        if (category) fd.append('category_Name', category.category_Name);
      }
      if (formData.type) {
        const type = allTypes.find(t => String(t.type_id) === String(formData.type));
        if (type) fd.append('type_name', type.type_name);
      }
      if (formData.variation) {
        const variation = allVariations.find(v => String(v.variation_id) === String(formData.variation));
        if (variation) fd.append('variation_name', variation.variation_name);
      }
      if (formData.implantCompany) {
        const company = allImplantCompanies.find(c => String(c.company_id) === String(formData.implantCompany));
        if (company) fd.append('implant_Company', company.company_name);
      }
      if (formData.implantConnection) {
        const brand = allBrands.find(b => String(b.brand_id) === String(formData.implantConnection));
        if (brand) fd.append('brand_Name', brand.brand_name);
      }
      if (formData.rawMaterial) fd.append('rawMaterial', formData.rawMaterial);
      if (formData.features) fd.append('features', formData.features);

      formData.imageFiles.forEach((file, index) => {
        if (file) fd.append(`image${index + 1}`, file);
      });

      fd.append('product_isNew', true);
      fd.append('shop_id', shopId);
      await axios.post(`${API_BASE_URL}/products/`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      clearProductsCache(shopId);
      await reloadProducts();
      closeModal();
      Swal.fire({ icon: 'success', title: 'Added!', text: 'Product added successfully', timer: 1500, showConfirmButton: false });
    } catch (err) {
      const data = err.response?.data || {};
      let title = 'Error';
      let text = data.error || err.message || 'Something went wrong';

      if (data.field === 'product_Name') {
        title = 'Duplicate Product Name';
        text = `Product "${formData.name}" already exists in this shop. Please use a different name.`;
      } else if (data.field === 'SKU') {
        title = 'Duplicate SKU';
        text = data.error || 'SKU already exists. Please try again.';
      }

      Swal.fire({
        icon: 'warning',
        title,
        text,
        confirmButtonColor: '#004aad'
      });
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = (product.product_Name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.SKU || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.reference || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.product_Description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !filters.type || product.type?.type_id?.toString() === filters.type;
    const matchesCategory = !filters.category || product.product_category?.catrgort_id?.toString() === filters.category;
    const matchesVariation = !filters.variation || product.product_variation?.variation_id?.toString() === filters.variation;
    const matchesCompany = !filters.implantCompany || product.implant_company?.company_id?.toString() === filters.implantCompany;
    const matchesConnection = !filters.implantConnection || product.product_brand?.brand_id?.toString() === filters.implantConnection;
    const productCity = product.city || product.selected_city || '';
    const matchesCity = !filters.city || filters.city === 'all' || filters.city === '' ||
      productCity === filters.city || getCityStocks(product).some((r) => r.city === filters.city);
    return matchesSearch && matchesType && matchesCategory && matchesVariation && matchesCompany && matchesConnection && matchesCity;
  });

  // ✅ PAGINATION: derived values (must stay above the early returns below)
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE));

  const paginatedProducts = useMemo(
    () => filteredProducts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [products, searchTerm, filters, page]
  );

  // ✅ PAGINATION: go back to page 1 when search / filters / shop change
  // (compares against previous key, so a restored page isn't reset on first mount)
  const prevPageKeyRef = useRef(JSON.stringify([searchTerm, filters, shopId]));
  useEffect(() => {
    const key = JSON.stringify([searchTerm, filters, shopId]);
    if (key !== prevPageKeyRef.current) {
      prevPageKeyRef.current = key;
      setPage(1);
    }
  }, [searchTerm, filters, shopId]);

  // ✅ PAGINATION: clamp page if it exceeds total (e.g. after delete)
  useEffect(() => {
    if (dataLoaded && page > totalPages) setPage(totalPages);
  }, [totalPages, dataLoaded, page]);

  // ✅ Scroll back to last edited product (jumps to the right page first)
  useEffect(() => {
    if (!dataLoaded) return;
    const id = sessionStorage.getItem('products_last_edited');
    if (!id) return;

    const idx = filteredProducts.findIndex((p) => String(p.product_Id) === String(id));
    if (idx === -1) return;

    const targetPage = Math.floor(idx / PAGE_SIZE) + 1;
    if (targetPage !== page) { setPage(targetPage); return; }

    const el = document.getElementById(`product-card-${id}`);
    if (el) {
      el.scrollIntoView({ block: 'center' });
      sessionStorage.removeItem('products_last_edited');
    }
  }, [dataLoaded, products.length, page]);

  const isOperationInProgress = false;

  const getDisplaySku = (product) => {
    const values = [];
    if (product.SKU) values.push(product.SKU);
    if (product.type?.sku) values.push(product.type.sku);
    if (product.product_variation?.sku) values.push(product.product_variation.sku);
    if (product.implant_company?.sku) values.push(product.implant_company.sku);
    if (product.product_brand?.sku) values.push(product.product_brand.sku);
    if (product.product_category?.sku) values.push(product.product_category.sku);
    return values;
  };

  const norm = (s) => String(s || '').toLowerCase().replace(/[-_/]/g, ' ');
  const catTypeText = (p) =>
    norm(`${p?.product_category?.category_Name || ''} ${p?.type?.type_name || ''}`);
  const nameText = (p) => norm(p?.product_Name);

  const detectLevel = (t) => {
    if (/tissue/.test(t)) return 'tissue';
    if (/implant\s*level/.test(t)) return 'implant';
    return '';
  };
  const getLevel = (p) => detectLevel(catTypeText(p)) || detectLevel(nameText(p));

  const isScrewProduct = (p) => {
    const ct = catTypeText(p).trim();
    return ct ? /screw/.test(ct) : /screw/.test(nameText(p));
  };

  const needsScrew = (p) =>
    /ti ?base|scan ?body/.test(`${catTypeText(p)} ${nameText(p)}`) &&
    !isScrewProduct(p) &&
    !!getLevel(p);

  const getScrewOptions = (product) => {
    if (!product) return [];
    const level = getLevel(product);
    if (!level) return [];
    return products.filter(
      (p) =>
        p.product_Id !== product.product_Id &&
        isScrewProduct(p) &&
        !!p.SKU &&
        getLevel(p) === level
    );
  };
  const displayCurrencyCode = (typeof getCurrentCurrency === 'function' && getCurrentCurrency()) || shopBaseCurrency || currency || 'PKR';
  const displayCurrencySymbol = (typeof getCurrentCurrencySymbol === 'function' && getCurrentCurrencySymbol()) || shopCurrencySymbol || shopBaseSymbol || getCurrencySymbol(displayCurrencyCode) || '';

  const moveCurrencySymbol = moveTargetCurrency && typeof getCurrencySymbol === 'function'
    ? (getCurrencySymbol(moveTargetCurrency) || '')
    : '';

  const formatProductPrice = (price) => {
    try {
      if (typeof formatPriceFromCode === 'function') return formatPriceFromCode(price, displayCurrencyCode);
      if (typeof formatInShopCurrency === 'function') return formatInShopCurrency(price);
      if (typeof formatPrice === 'function') return formatPrice(price);
      return `Rs.${Number(price || 0).toFixed(2)}`;
    } catch (e) { return `Rs.${Number(price || 0).toFixed(2)}`; }
  };

  if (!shopId) {
    return (
      <div className="product-details-content">
        <div className="product-header-section"><h2>Products List</h2></div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '80px 20px' }}>
          <FaBox style={{ fontSize: '64px', color: '#004aad', marginBottom: '20px' }} />
          <h3 style={{ color: '#004aad' }}>No Shop Selected</h3>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="product-details-content">
        <div className="product-header-section"><h2>Products {shopName}</h2></div>
        <div style={{ textAlign: 'center', padding: '60px', color: 'red' }}>
          <h3>Error: {error}</h3>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="product-details-content">
      <div className="products-action-section">
        <div className="products-heading">
          <h2>
            <FaBox className="products-heading-icon" />
            Products Management
            <span className="products-shop-name">— {shopName}</span>
          </h2>
        </div>

        <div className="products-action-right">
          <div className="products-search-wrapper">
            <FaSearch className="products-search-icon" />
            <input
              type="text"
              placeholder="Search by product name or SKU..."
              value={searchTerm}
              onChange={handleSearch}
              className="products-search-input"
            />
          </div>

          <div className="city-filter-inline" ref={cityDropdownRef}>
            <button
              type="button"
              className={`city-filter-inline-btn ${filters.city && filters.city !== 'all' ? 'active' : ''}`}
              onClick={() => setShowCityDropdown(!showCityDropdown)}
            >
              <FaCity className="city-filter-inline-icon" />
              <span>{filters.city && filters.city !== 'all' ? filters.city : 'All Cities'}</span>
              <FaChevronDown className={`city-filter-inline-arrow ${showCityDropdown ? 'rotated' : ''}`} />
            </button>

            {showCityDropdown && (
              <div className="city-filter-inline-dropdown">
                <button
                  type="button"
                  className={`city-filter-inline-item ${!filters.city || filters.city === 'all' ? 'selected' : ''}`}
                  onClick={() => { handleFilterChange('city', 'all'); setShowCityDropdown(false); }}
                >
                  <span>All Cities</span>
                  {(!filters.city || filters.city === 'all') && <FaCheck />}
                </button>
                {shopCities.map((city, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`city-filter-inline-item ${filters.city === city ? 'selected' : ''}`}
                    onClick={() => { handleFilterChange('city', city); setShowCityDropdown(false); }}
                  >
                    <span>{city}</span>
                    {filters.city === city && <FaCheck />}
                  </button>
                ))}
                {shopCities.length === 0 && <div className="city-filter-inline-empty">No cities available</div>}
              </div>
            )}
          </div>

          <div className="products-action-buttons">
            <button className="products-select-all-btn" onClick={handleSelectAllClick} disabled={isOperationInProgress}>
              <FaCheckDouble />
              <span>{selectMode ? 'Deselect All' : 'Select All'}</span>
            </button>
            <button className="products-add-btn" onClick={openAddModal} disabled={isOperationInProgress}>
              <FaPlus />
              <span>NEW PRODUCT</span>
            </button>
          </div>
        </div>
      </div>

      <div className="filters-sidebar">
        <div className="filters-header">
          <h3>Filters</h3>
          <button className="btn-clear-filters" onClick={clearFilters}>
            <FaTimes /> Clear All Filters
          </button>
        </div>
        <div className="filters-grid">
          <SearchableFilter label="Types" value={filters.type} options={typeOptions} count={typeOptions.length} onChange={(v) => handleFilterChange("type", v)} getOptionValue={(t) => t.type_id} getOptionLabel={(t) => t.type_name} placeholder="Select Type" />
          <SearchableFilter label="Categories" value={filters.category} options={categoryOptions} count={categoryOptions.length} onChange={(v) => handleFilterChange("category", v)} getOptionValue={(c) => c.catrgort_id} getOptionLabel={(c) => c.category_Name} placeholder="Select Category" />
          <SearchableFilter label="Variations" value={filters.variation} options={variationOptions} count={variationOptions.length} onChange={(v) => handleFilterChange("variation", v)} getOptionValue={(v) => v.variation_id} getOptionLabel={(v) => v.variation_name} placeholder="Select Variation" />
          <SearchableFilter label="Implant Companies" value={filters.implantCompany} options={companyOptions} count={companyOptions.length} onChange={(v) => handleFilterChange("implantCompany", v)} getOptionValue={(c) => c.company_id} getOptionLabel={(c) => c.company_name} placeholder="Select Company" />
          <SearchableFilter label="Implant Connection" value={filters.implantConnection} options={connectionOptions} count={connectionOptions.length} onChange={(v) => handleFilterChange("implantConnection", v)} getOptionValue={(b) => b.brand_id} getOptionLabel={(b) => b.brand_name} placeholder="Select Brand" />
        </div>
      </div>

      <div className="products-grid">
        {paginatedProducts.length > 0 ? (
          paginatedProducts.map(product => {
            const displaySku = getDisplaySku(product);
            const isImplant = isImplantProduct(product);
            const isSelected = selectedProducts.includes(product.product_Id);
            return (
              <div key={product.product_Id} id={`product-card-${product.product_Id}`} className="product-card">
                <div className="product-image-container">
                  <div className="product-select-checkbox">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleProductSelect(product.product_Id)}
                    />
                  </div>
                  {product.imageGallery?.image1 ? (
                    <img src={getImageUrl(product.imageGallery.image1)} alt={product.product_Name} className="product-image" loading="lazy" />
                  ) : (
                    <div className="product-image-placeholder">No Image</div>
                  )}
                </div>

                <div className="product-card-content">
                  <div className="product-name-row">
                    <h3 className="product-name">{product.product_Name}</h3>
                    {isSelected && (
                      <button
                        type="button"
                        className="btn-move-to"
                        onClick={() => openMoveModal(product)}
                      >
                        Move To
                      </button>
                    )}
                  </div>

                  {(product.product_Description || product.reference) && (
                    <div className="product-description-wrapper">
                      {product.product_Description && (
                        <p className={`product-description ${expandedDescriptions[product.product_Id] ? 'expanded' : 'collapsed'}`}>
                          {product.product_Description}
                        </p>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                        <div>
                          {product.product_Description && product.product_Description.length > 60 && (
                            <button className="read-more-link" onClick={() => setExpandedDescriptions(prev => ({ ...prev, [product.product_Id]: !prev[product.product_Id] }))}>
                              {expandedDescriptions[product.product_Id] ? 'Read Less' : 'Read More'}
                            </button>
                          )}
                        </div>
                        {product.reference && (
                          <span style={{ fontSize: '0.78rem', color: '#1e293b', fontWeight: '700' }}>
                            Ref: {product.reference}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="product-info-list">
                    <div className="info-item"><span className="info-item-label">Type</span><span className="info-item-value">{product.type?.type_name || 'N/A'}</span></div>
                    <div className="info-item"><span className="info-item-label">Company</span><span className="info-item-value">{product.implant_company?.company_name || 'N/A'}</span></div>
                    <div className="info-item">
                      <span className="info-item-label">SKU</span>
                      <span className="info-item-value">
                        {displaySku.length > 0 ? displaySku.map((v, i) => <span key={i}>{v}{i < displaySku.length - 1 && <br />}</span>) : 'N/A'}
                      </span>
                    </div>
                    <div className="info-item"><span className="info-item-label">Category</span><span className="info-item-value">{product.product_category?.category_Name || 'N/A'}</span></div>
                    <div className="info-item">
                      <span className="info-item-label">Price ({displayCurrencySymbol} {displayCurrencyCode})</span>
                      <span className="info-item-value price-value">{formatProductPrice(product.product_Price)}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-item-label">Stock</span>
                      <span className={`info-item-value stock-value ${product.product_Stock < 50 ? 'low-stock' : ''}`}>
                        {product.product_Stock} units
                        <CityStockSelect product={product} selectedCity={filters.city} />
                      </span>
                    </div>
                    <div className="info-item"><span className="info-item-label">Variation</span><span className="info-item-value">{product.product_variation?.variation_name || 'N/A'}</span></div>
                  </div>

                  <div className="product-actions">
                    <button className="btn-open-label" onClick={() => handleOpenLabel(product)}>
                      <FaEye />Open Label
                    </button>
                    <button className="btn-print-label" onClick={() => handlePrintLabel(product)}>
                      <FaPrint />{isImplant ? 'Tyvek Label' : 'Print Label'}
                    </button>
                    {(!ORDER_LABEL_ONLY_FOR_IMPLANT || isImplantProduct(product)) && (
                      <button className="btn-order-label" onClick={() => handleImplantOrderLabel(product)}>
                        <FaTag />{isImplant ? 'Implant Box Label' : 'Order Label'}
                      </button>
                    )}
                    <button className="btn-edit-product" onClick={() => handleEditProduct(product)}>
                      <FaEdit />Edit Product
                    </button>
                    <button className="btn-delete-product" onClick={() => handleDeleteProduct(product)}>
                      <RiDeleteBin5Line />Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        ) : dataLoaded ? (
          <div className="no-products">
            <FaBox className="no-products-icon" />
            <p>No products found</p>
            <button className="btn-clear-filters" onClick={clearFilters}>Clear Filters</button>
          </div>
        ) : null}
      </div>

      {/* ✅ PAGINATION */}
      {filteredProducts.length > 0 && (
        <TablePagination
          page={page}
          totalItems={filteredProducts.length}
          pageSize={PAGE_SIZE}
          onPageChange={(p) => {
            setPage(p);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        />
      )}

      {showMoveModal && productToMove && (
        <div className="modal-overlay" onClick={closeMoveModal}>
          <div className="move-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Move To</h2>
              <button className="modal-close" onClick={closeMoveModal}><FaTimesCircle /></button>
            </div>

            <div className="move-modal-body">
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b', textAlign: 'left' }}>
                Product: <b style={{ color: '#004aad' }}>{productToMove.product_Name}</b>
              </p>

              <label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', textAlign: 'left' }}>
                Select Shop
              </label>

              {otherShops.length > 0 ? (
                otherShops.map((shop) => {
                  const active = String(moveTargetShopId) === String(shop.shop_id);
                  return (
                    <button
                      key={shop.shop_id}
                      type="button"
                      className="move-shop-item"
                      style={active ? { background: '#004aad', color: '#ffffff', borderColor: '#004aad' } : undefined}
                      onClick={() => handleMoveShopSelect(shop.shop_id)}
                      disabled={moveLoading}
                    >
                      {shop.shop_name}
                    </button>
                  );
                })
              ) : (
                <div style={{ color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center', padding: '8px' }}>No other shops</div>
              )}

              {moveTargetShopId && (
                <div style={{ marginTop: '0.5rem', textAlign: 'left' }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', display: 'block', marginBottom: '8px' }}>
                    Price{moveTargetCurrency ? ` (${moveCurrencySymbol ? `${moveCurrencySymbol} ` : ''}${moveTargetCurrency})` : ''}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    className="city-stock-input"
                    style={{ width: '100%', flex: 'none', boxSizing: 'border-box', marginBottom: '14px' }}
                    placeholder={moveTargetCurrency ? `Enter price in ${moveTargetCurrency}` : 'Enter price'}
                    value={movePrice}
                    onChange={(e) => setMovePrice(e.target.value)}
                    disabled={moveLoading}
                  />

                  <label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', display: 'block', marginBottom: '8px' }}>
                    Stock by Cities
                  </label>
                  <div className="city-stock-rows">
                    {moveRows.map((row, index) => (
                      <div className="city-stock-row" key={index}>
                        <select
                          className="city-stock-select"
                          value={row.city}
                          onChange={(e) => updateMoveRow(index, 'city', e.target.value)}
                          disabled={moveTargetCities.length === 0 || moveLoading}
                        >
                          <option value="">Select City</option>
                          {moveTargetCities.map((city, i) => <option key={i} value={city}>{city}</option>)}
                        </select>
                        <input
                          type="number"
                          className="city-stock-input"
                          placeholder="Stock"
                          value={row.stock}
                          onChange={(e) => updateMoveRow(index, 'stock', e.target.value)}
                          disabled={moveLoading}
                        />
                        {index === moveRows.length - 1 ? (
                          <button type="button" className="btn-city-stock-add" onClick={addMoveRow} disabled={moveLoading}>+</button>
                        ) : (
                          <button type="button" className="btn-city-stock-remove" onClick={() => removeMoveRow(index)} disabled={moveLoading}>×</button>
                        )}
                      </div>
                    ))}
                  </div>
                  {moveTargetCities.length === 0 && (
                    <small style={{ color: '#94a3b8', display: 'block', marginTop: '6px' }}>No cities found for this shop.</small>
                  )}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={closeMoveModal} disabled={moveLoading}>Cancel</button>
              <button className="btn-submit" onClick={handleMoveSubmit} disabled={moveLoading || !moveTargetShopId}>
                <FaSave />{moveLoading ? 'Moving...' : 'Move Product'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && modalMode === 'add' && (
        <div className="modal-overlay">
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Product</h2>
              <button className="modal-close" onClick={closeModal}><FaTimesCircle /></button>
            </div>

            <div className="modal-body">
              <div className="image-previews-section">
                <h3>Image Previews</h3>
                <div className="image-previews-grid">
                  {[0, 1, 2, 3].map((index) => (
                    <div key={index} className="image-preview-card">
                      <div className="image-preview-label">Image {index + 1} Preview</div>
                      {formData.imagePreviews[index] ? (
                        <img src={formData.imagePreviews[index]} alt={`Preview ${index + 1}`} className="image-preview" />
                      ) : (<div className="image-preview-placeholder">No image</div>)}
                      <label className="image-upload-btn">
                        <FaUpload /> Upload Image
                        <input type="file" accept="image/*" onChange={(e) => handleImageUpload(index, e)} style={{ display: 'none' }} />
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="product-form">
                <div className="form-row">
                  <div className="form-group"><label>Product Name</label><input type="text" name="name" value={formData.name} onChange={handleFormChange} placeholder="Enter product name" /></div>
                  <div className="form-group"><label>SKU (Auto)</label><input type="text" name="sku" value={formData.sku} readOnly placeholder="Auto-generated" style={{ backgroundColor: '#f1f5f9', cursor: 'not-allowed' }} /><small style={{ color: '#64748b' }}>The number (###) is added automatically when you save.</small></div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Category (optional)</label>
                    <select name="category" value={formData.category} onChange={handleModalCategoryChange}>
                      <option value="">No Category</option>
                      {categories.map(cat => <option key={cat.catrgort_id} value={cat.catrgort_id}>{cat.category_Name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Implant Company</label>
                    <select name="implantCompany" value={formData.implantCompany} onChange={handleModalCompanyChange}>
                      <option value="">Select Company</option>
                      {implantCompanies.map(c => <option key={c.company_id} value={c.company_id}>{c.company_name}</option>)}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Implant Connection (Brand)</label>
                    <select name="implantConnection" value={formData.implantConnection} onChange={handleFormChange} disabled={!formData.implantCompany}>
                      <option value="">Select Brand</option>
                      {modalBrands.map(b => <option key={b.brand_id} value={b.brand_id}>{b.brand_name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Type (optional)</label>
                    <select name="type" value={formData.type} onChange={handleModalTypeChange}>
                      <option value="">No Type</option>
                      {modalTypes.map(type => <option key={type.type_id} value={type.type_id}>{type.type_name}</option>)}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group full-width">
                    <label>Variation (optional)</label>
                    <select name="variation" value={formData.variation} onChange={handleFormChange}>
                      <option value="">No Variation</option>
                      {modalVariations.map(v => <option key={v.variation_id} value={v.variation_id}>{v.variation_name}</option>)}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group full-width"><label>Reference (optional)</label><input type="text" name="reference" value={formData.reference} onChange={handleFormChange} /></div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>GTIN (optional)</label>
                    <input type="text" name="gtin" value={formData.gtin} onChange={handleFormChange} placeholder="e.g. 01234567891234" inputMode="numeric" />
                  </div>
                  <div className="form-group">
                    <label>Barcode Image (optional, 148 × 148)</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {formData.gtinImagePreview && (
                        <img
                          src={formData.gtinImagePreview}
                          alt="Barcode preview"
                          style={{ width: '48px', height: '48px', objectFit: 'contain', border: '1px solid #e2e8f0', borderRadius: '4px', background: '#fff' }}
                        />
                      )}
                      <label className="image-upload-btn" style={{ margin: 0 }}>
                        <FaUpload /> {formData.gtinImagePreview ? 'Change' : 'Upload'}
                        <input type="file" accept="image/*" onChange={handleGtinImageUpload} style={{ display: 'none' }} />
                      </label>
                      {formData.gtinImagePreview && (
                        <button type="button" onClick={clearGtinImage} style={{ border: 'none', background: 'transparent', color: '#dc2626', fontSize: '1.2rem', cursor: 'pointer' }} title="Remove">×</button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group full-width">
                    <label>Price ({displayCurrencySymbol} {displayCurrencyCode})</label>
                    <input type="text" name="price" value={formData.price} onChange={handleFormChange} placeholder={`Enter price in ${displayCurrencyCode}`} />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group full-width">
                    <label>Cities &amp; Stock</label>
                    <div className="city-stock-rows">
                      {formData.cityStockRows.map((row, index) => (
                        <div className="city-stock-row" key={index}>
                          <select className="city-stock-select" value={row.city} onChange={(e) => updateCityStockRow(index, 'city', e.target.value)} disabled={shopCities.length === 0}>
                            <option value="">Select City</option>
                            {shopCities.map((city, i) => <option key={i} value={city}>{city}</option>)}
                          </select>
                          <input type="number" className="city-stock-input" placeholder="Stock" value={row.stock} onChange={(e) => updateCityStockRow(index, 'stock', e.target.value)} />
                          {index === formData.cityStockRows.length - 1 ? (
                            <button type="button" className="btn-city-stock-add" onClick={addCityStockRow}>+</button>
                          ) : (
                            <button type="button" className="btn-city-stock-remove" onClick={() => removeCityStockRow(index)}>×</button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group full-width"><label>Raw Material</label><input type="text" name="rawMaterial" value={formData.rawMaterial} onChange={handleFormChange} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group full-width"><label>Features</label><input type="text" name="features" value={formData.features} onChange={handleFormChange} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group full-width"><label>Description</label><textarea name="description" value={formData.description} onChange={handleFormChange} rows="3" /></div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={closeModal}>Cancel</button>
              <button className="btn-submit" onClick={handleSubmit}><FaSave />Add Product</button>
            </div>
          </div>
        </div>
      )}

      {showPrintLabelModal && labelProduct && (<PrintLabelModal product={labelProduct} needsScrew={needsScrew(labelProduct)} screwOptions={getScrewOptions(labelProduct)} onClose={() => setShowPrintLabelModal(false)} />)}
      {showOpenLabelModal && labelProduct && (<OpenLabelModal product={labelProduct} needsScrew={needsScrew(labelProduct)} screwOptions={getScrewOptions(labelProduct)} onClose={() => setShowOpenLabelModal(false)} />)}
      {showImplantOrderLabel && labelProduct && (
        <ImplantOrderLabelModal
          product={labelProduct}
          onClose={() => setShowImplantOrderLabel(false)}
        />
      )}
    </div>
  );
};

export default Product;